import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-center gap-3 py-3 border-b border-background-200/70 last:border-b-0 hover:bg-background-100/50 px-2 rounded-md transition-colors duration-200"
      >
        <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{article.author}</span>
            <span className="whitespace-nowrap">{article.reads.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="flex-shrink-0 w-full sm:w-48 h-32 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {article.categoryName}
              </span>
              {article.isHot && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                  热门
                </span>
              )}
            </div>
            <h3 className="text-base font-semibold text-foreground-900 mt-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 mt-1 line-clamp-2">{article.excerpt}</p>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500 flex-wrap">
            <span className="whitespace-nowrap">
              <i className="ri-user-line mr-1" aria-hidden="true"></i>
              {article.author}
            </span>
            <span className="whitespace-nowrap">
              <i className="ri-eye-line mr-1" aria-hidden="true"></i>
              {article.reads.toLocaleString()} 阅读
            </span>
            <span className="whitespace-nowrap">
              <i className="ri-heart-line mr-1" aria-hidden="true"></i>
              {article.likes.toLocaleString()}
            </span>
            <span className="whitespace-nowrap">{article.updatedAt}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative overflow-hidden h-48">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-800 whitespace-nowrap">
            {article.categoryName}
          </span>
          {article.isHot && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
              热门
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/50">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="whitespace-nowrap">
              <i className="ri-eye-line mr-1" aria-hidden="true"></i>
              {article.reads >= 10000 ? `${(article.reads / 10000).toFixed(1)}万` : article.reads.toLocaleString()}
            </span>
            <span className="whitespace-nowrap">
              <i className="ri-heart-line mr-1" aria-hidden="true"></i>
              {article.likes >= 1000 ? `${(article.likes / 1000).toFixed(1)}k` : article.likes}
            </span>
          </div>
          <span className="text-xs text-foreground-400 whitespace-nowrap">{article.author}</span>
        </div>
      </div>
    </Link>
  );
}